import { getPathnameOnly, getPublicPath, localizePath, stripLocaleFromPath } from '@/lib/locales'

const STATIC_ROUTES = new Set([
  'contacts',
  'services',
  'search',
  'reviews',
  'cookie-policy',
  'patient-rights-and-responsibilities',
  'patient-and-family-bill-of-rights-and-responsibilities',
])

const NESTED_ROUTES = {
  blog: {
    pathname: '/blog/[articleSlug]',
    param: 'articleSlug',
  },
  wiki: {
    pathname: '/wiki/[wikiArticleSlug]',
    param: 'wikiArticleSlug',
  },
  specialists: {
    pathname: '/specialists/[specialistSlug]',
    param: 'specialistSlug',
  },
  promotions: {
    pathname: '/promotions/[promotionSlug]',
    param: 'promotionSlug',
  },
}

function parseQueryString(queryString = '') {
  if (!queryString) {
    return {}
  }

  return queryString.split('&').reduce((acc, part) => {
    const [key, value = ''] = part.split('=')

    if (!key) {
      return acc
    }

    acc[decodeURIComponent(key)] = decodeURIComponent(value)
    return acc
  }, {})
}

function buildAs(pathname, queryString) {
  return queryString ? `${pathname}?${queryString}` : pathname
}

export function getRouteFromPath(path = '/') {
  const cleanPath = stripLocaleFromPath(getPathnameOnly(path))
  const [pathname, queryString = ''] = cleanPath.split('?')
  const query = parseQueryString(queryString)
  const as = buildAs(pathname, queryString)
  const segments = pathname.split('/').filter(Boolean)

  if (!segments.length) {
    return { href: '/', as: '/' }
  }

  if (segments.length === 1) {
    const slug = segments[0]

    if (STATIC_ROUTES.has(slug)) {
      return { href: `/${slug}`, as }
    }

    return {
      href: { pathname: '/[typeSlug]', query: { ...query, typeSlug: slug } },
      as: pathname,
    }
  }

  if (segments.length === 2) {
    const [section, slug] = segments
    const nestedRoute = NESTED_ROUTES[section]

    if (nestedRoute) {
      return {
        href: {
          pathname: nestedRoute.pathname,
          query: { ...query, [nestedRoute.param]: slug },
        },
        as: pathname,
      }
    }

    return {
      href: {
        pathname: '/[typeSlug]/[serviceSlug]',
        query: { ...query, typeSlug: section, serviceSlug: slug },
      },
      as: pathname,
    }
  }

  return { href: pathname, as }
}

export function getLocalizedRoute(path = '/', locale) {
  const route = getRouteFromPath(path)
  const asPath = typeof route.as === 'string' ? route.as : getPathnameOnly(path)
  const [pathname, queryString = ''] = asPath.split('?')
  const localizedAs = buildAs(localizePath(pathname, locale), queryString)

  return {
    href: route.href,
    as: localizedAs,
  }
}

export function getLocalizedRouteFromRouter(router, locale) {
  return getLocalizedRoute(getPublicPath(router.asPath), locale)
}
